/**
 * Expense Modal
 * Modal for creating and editing expenses with receipt upload
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { useClientStore } from '../store/clientStore';
import {
  createExpense,
  updateExpense,
  uploadReceipt,
  EXPENSE_CATEGORIES
} from '../services/expenseService';
import './Modal.css';

const ExpenseModal = ({ expense, onClose, onSuccess }) => {
  const user = useAuthStore((state) => state.user);
  const clients = useClientStore((state) => state.clients);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    vendor: '',
    paymentMethod: 'credit_card',
    clientId: '',
    notes: '',
    billable: false,
    reimbursable: false,
    receiptUrl: '',
  });

  useEffect(() => {
    if (expense) {
      setFormData({
        category: expense.category,
        amount: expense.amount,
        date: new Date(expense.date.toDate()).toISOString().split('T')[0],
        description: expense.description,
        vendor: expense.vendor || '',
        paymentMethod: expense.paymentMethod || 'credit_card',
        clientId: expense.clientId || '',
        notes: expense.notes || '',
        billable: expense.billable || false,
        reimbursable: expense.reimbursable || false,
        receiptUrl: expense.receiptUrl || '',
      });
      if (expense.receiptUrl) {
        setReceiptPreview(expense.receiptUrl);
      }
    }
  }, [expense]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show local preview immediately
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setReceiptPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview('pdf');
    }

    try {
      setUploading(true);
      setError('');
      const url = await uploadReceipt(user.uid, file);
      handleChange('receiptUrl', url);
      setReceiptPreview(url);
    } catch (err) {
      setError(err.message);
      setReceiptPreview(null);
      handleChange('receiptUrl', '');
    } finally {
      setUploading(false);
    }
  };

  const removeReceipt = () => {
    setReceiptPreview(null);
    handleChange('receiptUrl', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.category) {
      setError('Please select a category');
      return;
    }

    if (!formData.amount || formData.amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setLoading(true);

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
      };

      if (expense) {
        await updateExpense(expense.id, data);
      } else {
        await createExpense(user.uid, data);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{expense ? 'Edit Expense' : 'Add Expense'}</h2>
          <button className="modal-close" onClick={onClose}>&#x2715;</button>
        </div>

        {error && <div className="error-message" style={{ margin: '0 1.5rem', padding: '0.75rem', background: '#fee2e2', color: '#991b1b', borderRadius: '0.375rem', fontSize: '0.875rem' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  className="input"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  required
                >
                  <option value="">Select category</option>
                  {EXPENSE_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="amount">Amount *</label>
                <input
                  id="amount"
                  type="number"
                  className="input"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  required
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">Date *</label>
                <input
                  id="date"
                  type="date"
                  className="input"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="paymentMethod">Payment Method</label>
                <select
                  id="paymentMethod"
                  className="input"
                  value={formData.paymentMethod}
                  onChange={(e) => handleChange('paymentMethod', e.target.value)}
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="cash">Cash</option>
                  <option value="gcash">GCash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="check">Check</option>
                  <option value="paypal">PayPal</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <input
                id="description"
                type="text"
                className="input"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                required
                placeholder="e.g., Monthly software subscription"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vendor">Vendor/Supplier</label>
              <input
                id="vendor"
                type="text"
                className="input"
                value={formData.vendor}
                onChange={(e) => handleChange('vendor', e.target.value)}
                placeholder="e.g., Adobe, Office Depot"
              />
            </div>

            {/* Receipt Upload Section */}
            <div className="form-group">
              <label>Receipt / Invoice</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {receiptPreview ? (
                  <div style={{
                    position: 'relative',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                    background: '#f8fafc',
                  }}>
                    {receiptPreview === 'pdf' ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        <span style={{ fontSize: '2rem' }}>PDF</span>
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>PDF receipt attached</p>
                      </div>
                    ) : (
                      <img
                        src={receiptPreview}
                        alt="Receipt preview"
                        style={{
                          width: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    )}
                    <button
                      type="button"
                      onClick={removeReceipt}
                      style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        background: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      aria-label="Remove receipt"
                    >
                      &#x2715;
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      border: '2px dashed var(--border-color)',
                      borderRadius: '0.5rem',
                      padding: '1.5rem',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s',
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem', opacity: 0.5 }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ display: 'inline-block' }}>
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      Click to upload receipt or invoice
                    </p>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      JPEG, PNG, WebP, HEIC, or PDF (max 10MB)
                    </p>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  capture="environment"
                  onChange={handleReceiptUpload}
                  style={{ display: 'none' }}
                />

                {!receiptPreview && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      style={{ flex: 1, fontSize: '0.875rem' }}
                    >
                      {uploading ? 'Uploading...' : 'Browse Files'}
                    </button>
                  </div>
                )}
              </div>
              <span className="help-text">Take a photo or upload an image of your receipt</span>
            </div>

            <div className="form-group">
              <label htmlFor="clientId">Related Client (Optional)</label>
              <select
                id="clientId"
                className="input"
                value={formData.clientId}
                onChange={(e) => handleChange('clientId', e.target.value)}
              >
                <option value="">None</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.billable}
                  onChange={(e) => handleChange('billable', e.target.checked)}
                />
                <span>Billable (can be charged to client)</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.reimbursable}
                  onChange={(e) => handleChange('reimbursable', e.target.checked)}
                />
                <span>Reimbursable (to be reimbursed to you)</span>
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                className="input"
                rows="3"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Additional notes..."
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || uploading}
            >
              {loading ? 'Saving...' : expense ? 'Update Expense' : 'Add Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseModal;
